/**
 * Argus — Wrapper fetch cu format JSON envelope unificat + cookie session + CSRF.
 *
 * - `credentials: 'include'` pentru cookie ARGUS_SSID cross-origin (Vite ↔ argus.local).
 * - Header `X-CSRF-Token` pe POST/PUT/PATCH/DELETE din `useAuthStore.getState().csrfToken`.
 * - La 401 cu sessionState=expired: dispatchEvent `argus:session-expired` → ReAuthModal.
 */

import { useAuthStore, type User, type UserPermission, type UserRole } from './auth';

export const SESSION_EXPIRED_EVENT = 'argus:session-expired';

export interface ApiPagination {
  readonly page: number;
  readonly perPage: number;
  readonly total: number;
  readonly totalPages: number;
}

export interface ApiMessage {
  readonly severity: 'info' | 'warning' | 'error' | 'success';
  readonly message: string;
}

export interface ApiEnvelope<T> {
  readonly sessionState: 'valid' | 'expired';
  readonly success: boolean;
  readonly data: T | null;
  readonly meta: {
    readonly timestamp: string;
    readonly pagination?: ApiPagination;
  };
  readonly messages: readonly ApiMessage[];
}

export class ApiError extends Error {
  readonly httpStatus: number;
  readonly messages: readonly ApiMessage[];

  constructor(httpStatus: number, messages: readonly ApiMessage[]) {
    const summary = messages[0]?.message ?? `HTTP ${httpStatus}`;
    super(summary);
    this.name = 'ApiError';
    this.httpStatus = httpStatus;
    this.messages = messages;
  }
}

export class SessionExpiredError extends ApiError {
  constructor(messages: readonly ApiMessage[]) {
    super(401, messages);
    this.name = 'SessionExpiredError';
  }
}

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://argus.local:8080';

const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function apiFetch<T>(
  path: string,
  init?: RequestInit
): Promise<{ data: T; pagination?: ApiPagination; messages: readonly ApiMessage[] }> {
  const url = `${baseUrl}${path}`;

  const headers = new Headers(init?.headers);
  const method = (init?.method ?? 'GET').toUpperCase();

  if (init?.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (STATE_CHANGING_METHODS.has(method)) {
    const csrfToken = useAuthStore.getState().csrfToken;
    if (csrfToken !== null && csrfToken !== '') {
      headers.set('X-CSRF-Token', csrfToken);
    }
  }

  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
  });

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(response.status, [
      { severity: 'error', message: `Invalid API response (HTTP ${response.status})` },
    ]);
  }

  if (envelope.sessionState === 'expired') {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    throw new SessionExpiredError(envelope.messages);
  }

  if (!envelope.success) {
    throw new ApiError(response.status, envelope.messages);
  }

  return {
    data: envelope.data as T,
    messages: envelope.messages,
    ...(envelope.meta.pagination ? { pagination: envelope.meta.pagination } : {}),
  };
}

// ---- Domain types + helpers ------------------------------------------------

export interface AgentMetrics {
  readonly cpuPct: number;
  readonly ramUsedMb: number;
  readonly ramTotalMb: number | null;
  readonly diskFreeGb: number;
  readonly diskTotalGb: number | null;
}

export interface MonitorInfo {
  readonly index: number;
  readonly width: number;
  readonly height: number;
  readonly isPrimary: boolean;
  readonly deviceName: string;
}

export interface Agent {
  readonly id: string;
  readonly hostname: string;
  readonly description: string | null;
  readonly osName: string | null;
  readonly osVersion: string | null;
  readonly osBuild: string | null;
  readonly osArch: string | null;
  readonly updateChannel: string;
  readonly agentVersion: string | null;
  readonly isOnline: boolean;
  readonly lastHeartbeatAt: string | null;
  readonly lastConnectedAt: string | null;
  readonly lastSeenUser: string | null;
  readonly lastSessionState: string | null;
  readonly lastDisconnectReason: string | null;
  readonly pcName: string | null;
  readonly windowsUsername: string | null;
  readonly monitors: readonly MonitorInfo[] | null;
  readonly activeStreamsCount: number;
  readonly lastMetrics: AgentMetrics | null;
  readonly lastAccessAt: string | null;
  readonly lastThumbnailAt: string | null;
  readonly heartbeatIntervalSeconds: number;
  readonly thumbnailIntervalSeconds: number;
  readonly enrolledAt: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export async function fetchAgents(): Promise<Agent[]> {
  const result = await apiFetch<Agent[]>('/api/v1/agents');
  return result.data;
}

export interface LoginResponse {
  readonly user: User;
  readonly csrfToken: string;
  readonly expiresAt: string;
  readonly permissions: readonly UserPermission[];
}

export async function loginRequest(
  username: string,
  password: string
): Promise<LoginResponse> {
  const result = await apiFetch<LoginResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  return result.data;
}

export async function logoutRequest(): Promise<void> {
  await apiFetch<null>('/api/v1/auth/logout', { method: 'POST' });
}

// Bloc v1.0.10 #A — change own password.
export async function changePasswordRequest(currentPassword: string, newPassword: string): Promise<void> {
  await apiFetch<{ changed: boolean }>('/api/v1/me/password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function fetchMe(): Promise<LoginResponse | null> {
  try {
    const result = await apiFetch<LoginResponse>('/api/v1/auth/me');
    return result.data;
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      return null;
    }
    throw error;
  }
}

// ---- Commands & Artifacts --------------------------------------------------

export type CommandStatusValue =
  | 'pending'
  | 'sent'
  | 'executing'
  | 'success'
  | 'failure'
  | 'expired'
  | 'cancelled';

export interface CommandStatus {
  readonly id: string;
  readonly agentId: string;
  readonly issuedBy: string;
  readonly command: string;
  readonly params: Record<string, unknown> | null;
  readonly justification: string;
  readonly status: CommandStatusValue;
  readonly sentAt: string | null;
  readonly ackedAt: string | null;
  readonly completedAt: string | null;
  readonly expiresAt: string;
  readonly resultData: Record<string, unknown> | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly artifactId: string | null;
  readonly createdAt: string;
}

export type CommandTypeValue =
  | 'screenshot'
  | 'file_read'
  | 'enumerate_devices'
  | 'stream_start'
  | 'stream_stop'
  | 'recording_start'
  | 'recording_stop'
  | 'set_watched_paths';

export interface EmitCommandRequest {
  readonly command: CommandTypeValue;
  readonly justification: string;
  readonly params?: Record<string, unknown>;
}

export interface EmitCommandResponse {
  readonly commandId: string;
  readonly status: CommandStatusValue;
  readonly expiresAt: string;
}

export async function emitCommand(
  agentId: string,
  request: EmitCommandRequest
): Promise<EmitCommandResponse> {
  const result = await apiFetch<EmitCommandResponse>(
    `/api/v1/agents/${agentId}/commands`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
  return result.data;
}

export async function fetchCommandStatus(commandId: string): Promise<CommandStatus> {
  const result = await apiFetch<CommandStatus>(`/api/v1/commands/${commandId}`);
  return result.data;
}

export function artifactDownloadUrl(artifactId: string): string {
  return `${baseUrl}/api/v1/artifacts/${artifactId}/download`;
}

export const TERMINAL_COMMAND_STATUSES: ReadonlySet<CommandStatusValue> = new Set([
  'success',
  'failure',
  'expired',
  'cancelled',
]);

// ---- Users + permissions admin endpoints ----------------------------------

export interface AdminUser {
  readonly id: string;
  readonly username: string;
  readonly email: string;
  readonly role: UserRole;
  readonly fullName: string | null;
  readonly isActive: boolean;
}

export async function fetchUsers(): Promise<AdminUser[]> {
  const result = await apiFetch<AdminUser[]>('/api/v1/users');
  return result.data;
}

export async function fetchUserPermissions(userId: string): Promise<UserPermission[]> {
  const result = await apiFetch<UserPermission[]>(`/api/v1/users/${userId}/permissions`);
  return result.data;
}

export async function fetchMyPermissions(): Promise<UserPermission[]> {
  const result = await apiFetch<UserPermission[]>('/api/v1/me/permissions');
  return result.data;
}

export async function grantPermission(
  userId: string,
  request: { agentId: string; permission: 'read' | 'command' | 'admin' }
): Promise<UserPermission> {
  const result = await apiFetch<UserPermission>(`/api/v1/users/${userId}/permissions`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return result.data;
}

export async function revokePermission(userId: string, permId: number): Promise<void> {
  await apiFetch<{ id: number; revokedAt: string }>(
    `/api/v1/users/${userId}/permissions/${permId}`,
    { method: 'DELETE' }
  );
}
